import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import MoreHorizRounded from '@mui/icons-material/MoreHorizRounded';
import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useDataModels, type DataModel } from '../features/dataio/api';
import { ExportDialog } from '../features/dataio/ExportDialog';

/** A command of the page (Cancel, Close short…). */
export interface MoreAction {
  key: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
  danger?: boolean;
}

/** Documents linked to this one (its deliveries, invoices, payments…), listed only when there are any. */
export interface LinkedGroup {
  key: string;
  title: string;
  icon: ReactNode;
  docs: { id: string; label: string; hint?: string; to: string }[];
  /** How many there are, when `docs` is only the latest of them. */
  total?: number;
  /** Where all of them are listed, shown when there are more than fit. */
  allTo?: string;
}

const SHOWN = 5;

/**
 * The ⋯ menu of a record page: the documents linked to it (each group only once it has any), then
 * Duplicate and Export, then the page's own commands. Hidden when it would be empty.
 */
export function MoreMenu({
  linked = [],
  onDuplicate,
  exportModel,
  exportId,
  actions = [],
}: {
  linked?: LinkedGroup[];
  onDuplicate?: () => void;
  /** Data model to export this record as (e.g. "sales_order"), when the user may export it. */
  exportModel?: string;
  exportId?: string;
  actions?: MoreAction[];
}) {
  const navigate = useNavigate();
  const models = useDataModels();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [exporting, setExporting] = useState<DataModel | null>(null);
  const model = exportModel
    ? models.data?.find((m) => m.name === exportModel && m.canExport)
    : undefined;
  const groups = linked.filter((g) => g.docs.length > 0);
  if (groups.length === 0 && !onDuplicate && !model && actions.length === 0) return null;

  const close = () => setAnchor(null);
  const go = (to: string) => {
    close();
    void navigate(to);
  };
  // Flat children: MUI menus navigate by keyboard only through direct MenuItem children.
  const sections: ReactNode[][] = [];
  for (const g of groups) {
    const count = Math.max(g.total ?? 0, g.docs.length);
    sections.push([
      <ListSubheader
        key={`${g.key}-title`}
        sx={{ lineHeight: '32px', fontSize: 11.5, fontWeight: 700, letterSpacing: '0.06em' }}
      >
        {g.title.toUpperCase()} ({count})
      </ListSubheader>,
      ...g.docs.slice(0, SHOWN).map((d) => (
        <MenuItem key={`${g.key}-${d.id}`} onClick={() => go(d.to)}>
          <ListItemIcon sx={{ color: 'text.secondary' }}>{g.icon}</ListItemIcon>
          <ListItemText
            primary={d.label}
            secondary={d.hint}
            slotProps={{
              primary: { sx: { fontWeight: 600, fontSize: 14 } },
              secondary: { sx: { fontSize: 12 } },
            }}
          />
          <ChevronRightRounded fontSize="small" sx={{ color: 'text.disabled', ml: 1 }} />
        </MenuItem>
      )),
      ...(count > SHOWN && g.allTo
        ? [
            <MenuItem key={`${g.key}-all`} onClick={() => go(g.allTo ?? '')}>
              <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                All {count} {g.title.toLowerCase()}
              </Typography>
            </MenuItem>,
          ]
        : []),
    ]);
  }
  const tools: ReactNode[] = [];
  if (onDuplicate)
    tools.push(
      <MenuItem
        key="duplicate"
        onClick={() => {
          close();
          onDuplicate();
        }}
      >
        <ListItemIcon>
          <ContentCopyRounded fontSize="small" />
        </ListItemIcon>
        <ListItemText>Duplicate</ListItemText>
      </MenuItem>,
    );
  if (model)
    tools.push(
      <MenuItem
        key="export"
        onClick={() => {
          close();
          setExporting(model);
        }}
      >
        <ListItemIcon>
          <DownloadRounded fontSize="small" />
        </ListItemIcon>
        <ListItemText>Export…</ListItemText>
      </MenuItem>,
    );
  if (tools.length) sections.push(tools);
  if (actions.length)
    sections.push(
      actions.map((a) => (
        <MenuItem
          key={`action-${a.key}`}
          onClick={() => {
            close();
            a.onClick();
          }}
          sx={a.danger ? { color: 'error.main' } : undefined}
        >
          <ListItemIcon sx={{ color: 'inherit' }}>{a.icon}</ListItemIcon>
          <ListItemText>{a.label}</ListItemText>
        </MenuItem>
      )),
    );

  return (
    <>
      <Tooltip title="More">
        <IconButton
          aria-label="More actions"
          aria-haspopup="menu"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
        >
          <MoreHorizRounded />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 260, maxHeight: 480 } } }}
      >
        {sections.flatMap((items, i) =>
          i > 0 ? [<Divider key={`divider-${String(i)}`} />, ...items] : items,
        )}
      </Menu>
      {exporting && exportId && (
        <ExportDialog model={exporting} ids={[exportId]} open onClose={() => setExporting(null)} />
      )}
    </>
  );
}
